import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

export default function TermsOfService() {
  return (
    <div data-testid="terms-page">
      <section className="border-b border-zinc-800 bg-zinc-950">
        <div className="container-pl py-20">
          <p className="overline mb-5">Legal</p>
          <h1 className="display-serif text-5xl lg:text-6xl text-zinc-50 leading-[1.05] mb-4">
            Terms of Service
          </h1>
          <p className="text-sm text-zinc-400 font-mono tracking-wider">Last updated: February 2026</p>
        </div>
      </section>

      {/* Prominent "Not a Law Firm" disclaimer */}
      <section className="border-b border-zinc-800 bg-amber-950/40">
        <div className="container-pl py-8">
          <div className="flex items-start gap-4 max-w-3xl" data-testid="not-law-firm-disclaimer">
            <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
            <div>
              <p className="font-medium text-zinc-50 mb-1">Important: We are not a law firm.</p>
              <p className="text-sm text-zinc-200 leading-relaxed">
                Paralegal Voices of New York provides <span className="font-medium">paralegal services only</span>.
                We are not attorneys, we do not provide legal advice, and we do not represent clients
                in court. For legal advice, consult a licensed attorney.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-pl py-20 max-w-3xl">
        <div className="space-y-10 text-zinc-200 leading-relaxed">
          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">1. Acceptance of Terms</h2>
            <p className="text-base">
              By using this site or engaging our services, you agree to these Terms of Service. If
              you do not agree, please do not use our services.
            </p>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">2. Nature of Services</h2>
            <p className="text-base mb-3">
              Paralegal Voices of New York provides <strong>non-attorney paralegal services</strong>,
              including document preparation, case organization, hearing preparation support, and
              related services. We are not a law firm.
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-base text-zinc-300">
              <li>We do not provide legal advice.</li>
              <li>We do not represent clients in court.</li>
              <li>We do not establish attorney-client relationships.</li>
              <li>All documents we prepare are reviewed by a licensed attorney before delivery.</li>
            </ul>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">3. Use of the Site</h2>
            <p className="text-base">
              You agree to use this site only for lawful purposes and in a way that does not infringe
              the rights of others. You will not post defamatory, abusive, threatening, harassing, or
              illegal content in the community forum.
            </p>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">4. Community Forum</h2>
            <p className="text-base">
              The community forum is a peer-support space. Posts and comments reflect the views of
              their authors, not of Paralegal Voices of New York. We reserve the right to moderate,
              edit, or remove content that violates these Terms.
            </p>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">5. Fees & Payment</h2>
            <p className="text-base">
              Fees for services are discussed transparently during the initial consultation. Specific
              terms — including refund and cancellation policies — are agreed to in writing before
              any work begins.
            </p>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">6. Confidentiality</h2>
            <p className="text-base">
              We treat client information as confidential. See our{" "}
              <Link to="/privacy" className="text-amber-400 link-underline">Privacy Policy</Link> for
              details.
            </p>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">7. Limitation of Liability</h2>
            <p className="text-base">
              To the maximum extent permitted by law, Paralegal Voices of New York is not liable for
              any indirect, incidental, or consequential damages arising from your use of the site or
              our services. Our total liability is limited to the fees paid for the specific service
              giving rise to the claim.
            </p>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">8. Changes to These Terms</h2>
            <p className="text-base">
              We may update these Terms from time to time. Continued use of the site after changes
              are posted constitutes acceptance of the revised Terms.
            </p>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">9. Governing Law</h2>
            <p className="text-base">
              These Terms are governed by the laws of the State of New York. Any disputes will be
              handled in the courts of New York State.
            </p>
          </div>

          <div>
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">10. Contact</h2>
            <p className="text-base">
              Questions? Email{" "}
              <a href="mailto:Darrell@paralegalvoicesofnewyork.com" className="text-amber-400 link-underline">
                Darrell@paralegalvoicesofnewyork.com
              </a>
              .
            </p>
          </div>

          <div className="pt-8 border-t border-zinc-800">
            <Link to="/privacy" className="text-sm text-amber-400 link-underline">
              View Privacy Policy →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
